import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  Loader2, 
  FileText,
  Sparkles,
  X,
  Coins,
  Link2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// Admin email bypasses credit checks
const ADMIN_EMAIL = 'pgundepudi@gmail.com';

const PAGE_CREDIT_COST = 5;

const PAGE_SUGGESTIONS = [
  { name: 'Portfolio', purpose: 'Showcase our work, projects, and case studies' },
  { name: 'Pricing', purpose: 'Display pricing plans, packages, and subscription options' },
  { name: 'FAQ', purpose: 'Answer frequently asked questions from customers' },
  { name: 'Blog', purpose: 'Share articles, news, and industry insights' },
  { name: 'Testimonials', purpose: 'Display customer reviews and success stories' },
  { name: 'Team', purpose: 'Introduce team members and company leadership' },
  { name: 'Careers', purpose: 'List job openings and company culture' },
  { name: 'Partners', purpose: 'Showcase partnerships and integrations' }
];

const AddPageModal = ({ projectId, designId, credits, onClose, onSuccess, userEmail }) => {
  const [pageName, setPageName] = useState('');
  const [pagePurpose, setPagePurpose] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isAdmin = userEmail === ADMIN_EMAIL;

  const handleSuggestionClick = (suggestion) => {
    setPageName(suggestion.name);
    setPagePurpose(suggestion.purpose);
  };

  const handleGenerate = async () => {
    if (!pageName.trim()) {
      toast.error('Please enter a page name');
      return;
    }
    if (!pagePurpose.trim()) {
      toast.error('Please describe the page purpose');
      return;
    }
    // Admin completely bypasses ALL credit checks
    // Non-admin users still need credits
    if (!isAdmin && credits < PAGE_CREDIT_COST) {
      toast.error(`Insufficient credits. Adding a page costs ${PAGE_CREDIT_COST} credits.`);
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/ai/generate-page`,
        {
          project_id: projectId,
          design_id: designId,
          page_name: pageName.trim(),
          page_purpose: pagePurpose.trim(),
          reference_url: referenceUrl.trim() || null
        },
        { withCredentials: true }
      );

      toast.success(`"${pageName}" page generated successfully!`);
      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      console.error('Page generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate page');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <Plus className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Add New Page</h2>
              <p className="text-sm text-zinc-500">AI will generate a page based on your inputs</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 text-2xl"
            data-testid="close-add-page-modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Credit Cost Banner - Hidden for admin */}
          {!isAdmin && (
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  Cost: <strong>{PAGE_CREDIT_COST} credits</strong>
                </span>
              </div>
              <span className="text-sm text-amber-600">
                Your balance: {credits} credits
              </span>
            </div>
          )}

          {/* Page Name */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Page Name *
            </label>
            <Input
              data-testid="page-name-input"
              placeholder="e.g., Portfolio, Pricing, FAQ"
              value={pageName}
              onChange={(e) => setPageName(e.target.value)}
              className="bg-white"
            />
          </div>

          {/* Page Purpose */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Page Purpose *
            </label>
            <Textarea
              data-testid="page-purpose-input"
              placeholder="Describe what this page should contain and its goal..."
              value={pagePurpose}
              onChange={(e) => setPagePurpose(e.target.value)}
              className="min-h-[100px] bg-white"
            />
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
            data-testid="toggle-advanced-options"
          >
            <Eye className="w-4 h-4" />
            {showAdvanced ? 'Hide' : 'Show'} Advanced Options
          </button>

          {/* Reference URL (Advanced) */}
          {showAdvanced && (
            <div className="space-y-3 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  <Link2 className="w-4 h-4 inline mr-1" />
                  Reference URL (Optional)
                </label>
                <Input
                  data-testid="reference-url-input"
                  placeholder="https://example.com/their-pricing-page"
                  value={referenceUrl}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                  className="bg-white"
                  type="url"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Provide a URL of a similar page you like. AI will analyze its structure and content style.
                </p>
              </div>
            </div>
          )}

          {/* Quick Suggestions */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Quick Suggestions
            </label>
            <div className="flex flex-wrap gap-2">
              {PAGE_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion.name}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    pageName === suggestion.name
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                      : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                  }`}
                  data-testid={`suggestion-${suggestion.name.toLowerCase()}`}
                >
                  {suggestion.name}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
            <Sparkles className="w-4 h-4 text-indigo-600 mt-0.5" />
            <p className="text-xs text-indigo-700">
              The AI will use your business briefing, selected theme, and existing design style to create a cohesive new page that matches your website.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zinc-200 bg-zinc-50 rounded-b-2xl">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || !pageName.trim() || !pagePurpose.trim() || (!isAdmin && credits < PAGE_CREDIT_COST)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="generate-page-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Generate Page
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPageModal;
